import { ReactElement } from "react";
import { Avatar } from "@twilio-paste/core/avatar";
import {
  ChatBubble,
  ChatMessage as PasteChatMessage,
  ChatMessageMeta,
  ChatMessageMetaItem,
} from "@twilio-paste/core/chat-log";
import { Box } from "@twilio-paste/core/box";
import { Button } from "@twilio-paste/core/button";
import { Text } from "@twilio-paste/core/text";
import { ErrorIcon } from "@twilio-paste/icons/esm/ErrorIcon";
import { UserIcon } from "@twilio-paste/icons/esm/UserIcon";
import { styled } from "@twilio-paste/core/styling-library";

import { DateTime } from "@twiliointernal/org/twilio/e2-commons/shared/utils";
import { MarkdownPreview } from "./sub";
import { copy, constants, types } from "./definitions";
import styles from "./css/style.module.css";
import wiseOwlEmoji from "../../assets/wise_owl_emoji.png";

const ChatMessageContainer = styled(Box)`
  [data-paste-element="CHAT_BUBBLE"] {
    max-width: 100%;
    width: auto;

    /* Paste Chat Bubble includes whitespace that is distorting chat bubble size 
    and affecting rendering of markdown */
    white-space: unset;
    overflow-x: auto;

    /* We will be improving markdown rendering in the future. For now this makes code blocks scrollable */
    pre {
      white-space: pre;
      overflow-x: auto;
      max-width: 100%;
    }
  }
`;

const ChatMessage = ({
  author,
  body,
  createdAt,
  htmlBody,
  hasError,
  handleResend,
  retryId,
}: types.ChatMessageData): ReactElement => {
  const { displayName, isAuthor, isEndUser } = author;

  const dateTime = new DateTime(createdAt);
  const formattedDate = dateTime.format(constants.MESSAGE_DATE_FORMAT);
  const formattedTime = dateTime.format(constants.MESSAGE_TIME_FORMAT);

  const variant = isAuthor ? "outbound" : "inbound";

  /**
   * Markdown support we want to include only for WiseOwl
   */
  const markdownValue = !isEndUser ? body : htmlBody;

  const renderMessageAvatar = () => {
    if (isEndUser) {
      return <Avatar size="sizeIcon20" name={displayName} icon={UserIcon} />;
    }

    return <Box as="img" src={wiseOwlEmoji} size={"sizeIcon20"} />;
  };

  return (
    <ChatMessageContainer>
      <PasteChatMessage variant={variant}>
        <ChatBubble>
          <MarkdownPreview value={markdownValue} isHtml={isEndUser} />
        </ChatBubble>

        {hasError && isEndUser ? (
          <Box className={styles.retryMessageContainer}>
            <ErrorIcon
              color={"colorTextError"}
              size={"sizeIcon20"}
              decorative
            />
            <Text color={"colorTextError"} paddingRight="space30" as="p">
              {copy.messages.retryText}
            </Text>
            <Button
              variant={"link"}
              onClick={() => {
                if (handleResend && retryId) {
                  handleResend(retryId);
                }
              }}
            >
              Resend
            </Button>
          </Box>
        ) : (
          <ChatMessageMeta
            aria-label={copy.messages.eventLabel(displayName, formattedDate)}
          >
            <ChatMessageMetaItem>
              {/* if author is WiseOwl, render icon instead of default Paste avatar  */}
              <Box
                as="span"
                position={["absolute", "relative"]}
                top={[".5rem", "0"]}
                left="0"
              >
                {renderMessageAvatar()}
              </Box>
              <Box as="span" display={["none", "inline"]}>
                {copy.messages.timeStamp(displayName, formattedTime)}
              </Box>
            </ChatMessageMetaItem>
          </ChatMessageMeta>
        )}
      </PasteChatMessage>
    </ChatMessageContainer>
  );
};

export default ChatMessage;
